import React, { type JSX } from 'react';
import { IconButton, Box, Toolbar, Typography, Skeleton } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { AppBar, Spacer, ThreeLiner } from '@brightlayer-ui/react-components';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { GroupPath, HeaderActions } from './';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';
import { RootState, useTypedSelector } from '@fiji/common/src/app/store';
// import backgroundImage from '../assets/images/collapsible_app_bar.jpg';
import { useNavigationBack, useRBAC } from 'hooks';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';

export const useStyles = makeStyles((theme: Theme) => ({
    title: {},
    subtitle: {},
    info: {},
    liner: {
        top: 0,
        position: 'absolute',
        flexGrow: 1,
        marginLeft: 56,
        [theme.breakpoints.down('md')]: {
            marginLeft: 56,
        },
    },
    toolbarRightItem: {
        marginRight: theme.spacing(3),
    },
    expanded: {
        '& $liner': {
            top: 80,
        },
    },
    collapsed: {
        '& $title': {
            fontSize: '1.25rem',
            fontWeight: 600,
        },
        '& $subtitle': {
            fontSize: 0,
        },
        '& $info': {
            fontSize: '1rem',
            marginTop: '-0.25rem',
            fontWeight: 400,
        },
        '& $backgroundGradient': {
            backgroundImage: 'none !important',
        },
    },
    collapsedUpcoming: {
        '& $title': {
            fontSize: '1.25rem',
            fontWeight: 600,
        },
        '& $info': {
            display: 'none',
        },
        '& $backgroundGradient': {
            backgroundImage: 'none !important',
        },
    },
    userMenuButton: {
        // height:'100% !important',
    },
    toolbarGutters: {
        paddingLeft: 16,
        paddingRight: 16,
        minHeight: '56px',
        [theme.breakpoints.down('sm')]: {
            minHeight: '56px',
        },
    },
    normalGradient: {
        background: `rgba(0, 123, 193)  !important`,
    },
    warningGradient: {
        background: `rgba(244,119,33) !important`,
    },
    alertGradient: {
        background: `rgba(200, 60, 61) !important`,
    },
    offlineGradient: {
        background: `rgba(105,177,195)  !important`,
    },
    quarantineGradient: {
        background: ` #945D83 !important`,
    },
    snoozeGradient: {
        background: ` #983FEF !important`,
    },
    backGroundGradient: {
        opacity: '0.1!important',
    },
    backGroundGradientServicePortal: {
        backgroundColor: '#DD6010 !important',
    },
    toolbarRightContent: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
}));

const resolveSubtitleByPermission = (
    canViewAlert: boolean,
    canViewStatus: boolean,
    connectionStatus: string,
    deviceStatus: string
): string => {
    const isOnline = connectionStatus === 'Normal';
    if (!canViewAlert && !canViewStatus) {
        return '';
    }
    if (!canViewAlert) {
        return isOnline ? 'Online' : connectionStatus ?? 'N/A';
    }
    if (!canViewStatus) {
        return deviceStatus ?? 'N/A';
    }
    if (deviceStatus && isOnline) {
        return `Online • ${deviceStatus}`;
    }
    return deviceStatus ?? '';
};

const getGradientClasses = (
    classes: any,
    data: any,
    canViewAlert: boolean,
    snoozeIsVisible: boolean
): Record<string, boolean> => {
    const { deviceStatus, isUpcomingServicePortal } = data ?? {};
    const normalizedHealth = data?.health?.toUpperCase?.() ?? '';
    const isAlarmHealth = normalizedHealth === 'ALARM';
    const isWarningHealth = normalizedHealth === 'WARNING';
    const isNormalHealth = normalizedHealth === 'NORMAL';
    return {
        [classes.normalGradient]: deviceStatus === 'Normal' || !canViewAlert || isNormalHealth,
        [classes.offlineGradient]: deviceStatus === 'Offline' && canViewAlert,
        // health-based colors take priority over deviceStatus for upcoming pages
        [classes.alertGradient]: (deviceStatus === 'Alarm' || isAlarmHealth) && canViewAlert,
        [classes.snoozeGradient]: (snoozeIsVisible === true || deviceStatus === 'Snoozed') && canViewAlert,
        [classes.backGroundGradientServicePortal]:
            isUpcomingServicePortal && !isAlarmHealth && !isWarningHealth && !isNormalHealth && canViewAlert,
        [classes.quarantineGradient]: deviceStatus === 'Quarantined' && canViewAlert,
        [classes.warningGradient]: (deviceStatus === 'Warning' || isWarningHealth) && canViewAlert,
    };
};

const renderHeaderNote = (data: any): JSX.Element | boolean =>
    data?.note && <Typography variant="body2">{`Note: ${data?.note}`}</Typography>;

const renderHeaderInfo = (isLoading: boolean, data: any): JSX.Element => {
    if (isLoading) {
        return (
            <Skeleton animation="wave">
                <Typography variant="body2">{'group > path'}</Typography>
            </Skeleton>
        );
    }
    if (!data?.isUpcomingServicePortal) {
        return (
            <>
                <GroupPath
                    deviceName={data?.deviceName ?? data?.name}
                    pathName={data?.groupPath ? [...data.groupPath] : []}
                />
                {renderHeaderNote(data)}
            </>
        );
    }
    return <>{renderHeaderNote(data)}</>;
};

/* The code defines a functional component called `AppCollapsibleHeader`. It takes in a `props` object
with two properties: `page`. */
export const AppCollapsibleHeader = (props: { page: JSX.Element }): JSX.Element => {
    const theme = useTheme();
    const classes = useStyles(theme);
    const { page } = props;
    const location = useLocation();
    const currentRealmName = useTypedSelector((state: RootState) => state.common.selectedRealm);

    const data = useTypedSelector((state: any) => state?.common.selectedNode);
    const isLoading = useTypedSelector((state: any) => state?.common?.selectedNodeLoader);
    const snoozeIsVisible: boolean = useTypedSelector((state) => state.alarmtimeline.snoozeIsVisible);
    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);
    const canViewAlert = hasPermission('display-alerts_view-notifications');
    const canViewStatus = hasPermission('display-device-status_view-notifications');
    /**
     * The function `handleSubtitle` returns a string or JSX element that represents the status of the data.
     * If `isLoading` is true, it returns a skeleton loader with "Offline" text.
     * If `data?.isUpcomingServicePortal` is true, it returns the `data?.deviceName` or 'N/A' if `deviceName` is not available.
     * If `data?.deviceStatus` is not available, it returns "N/A".
     * If `data?.deviceStatus` is available and `data?.connectionStatus` is "Online", it returns "Online • {data.deviceStatus}".
     * Otherwise, it returns the `data?.deviceStatus`.
     *
     * @returns {string | JSX.Element} The status of the data or a skeleton loader.
     */
    const handleSubtitle = (): string | JSX.Element => {
        if (isLoading) {
            return (
                <Skeleton animation="wave">
                    <Typography variant="body2">Offline</Typography>
                </Skeleton>
            );
        }

        const { isUpcomingServicePortal, deviceName, connectionStatus, deviceStatus } = data ?? {};

        if (isUpcomingServicePortal) {
            return deviceName ?? 'N/A';
        }
        return resolveSubtitleByPermission(canViewAlert, canViewStatus, connectionStatus, deviceStatus);
    };

    const deviceNameContentHandler = (): any => {
        if (isLoading) {
            return (
                <Skeleton animation="wave">
                    <Typography variant="h5">Devicename009218</Typography>
                </Skeleton>
            );
        } else if (data?.isUpcomingServicePortal || data?.isUpcoming) {
            return data?.name;
        }
        return data?.deviceName ?? data?.name ?? '';
    };

    const navigationBack = useNavigationBack();
    const handleNavigateBack = (): void => {
        navigationBack();
    };

    const isUpcomingPage = Boolean(data?.isUpcoming);
    const appBarExpandedHeight = isUpcomingPage ? 220 : 260;
    const appBarScrollThreshold = isUpcomingPage ? 1 : 136;
    const appBarAnimationDuration = isUpcomingPage ? 150 : 300;

    return (
        <Box style={{ minHeight: '100vh' }}>
            <AppBar
                className={clsx(getGradientClasses(classes, data, canViewAlert, snoozeIsVisible))}
                expandedHeight={appBarExpandedHeight}
                collapsedHeight={56}
                scrollThreshold={appBarScrollThreshold}
                animationDuration={appBarAnimationDuration}
                // backgroundImage={data?.image ?? backgroundImage}
                variant={'snap'}
                classes={{
                    collapsed: isUpcomingPage ? classes.collapsedUpcoming : classes.collapsed,
                    expanded: classes.expanded,
                    background: classes.backGroundGradient,
                }}
                id="app-collapsible-header"
            >
                <Toolbar data-cy={'toolbar'} classes={{ gutters: classes.toolbarGutters }}>
                    {location.pathname !== `/${currentRealmName}` && (
                        <IconButton
                            data-cy="toolbar-menu"
                            color={'inherit'}
                            edge={'start'}
                            size="large"
                            onClick={handleNavigateBack}
                        >
                            <ArrowBack />
                        </IconButton>
                    )}
                    <Spacer />
                    <ThreeLiner
                        classes={{ title: classes.title, subtitle: classes.subtitle, info: classes.info }}
                        className={classes.liner}
                        title={deviceNameContentHandler()}
                        subtitle={handleSubtitle()}
                        info={renderHeaderInfo(isLoading, data)}
                        animationDuration={appBarAnimationDuration}
                    />
                    <Box className={classes.toolbarRightContent}>
                        <HeaderActions isUpcomingServicePortal={data?.isUpcomingServicePortal} />
                    </Box>
                </Toolbar>
            </AppBar>
            <Box>{page}</Box>
        </Box>
    );
};
